/**
 * Full-canvas overlay for cache debug inspection.
 *
 * Slides up over the flow canvas (same pattern as PlaybookOverlay).
 * Body is shared with the RunDebugDrawer's Cache tab via {@link CacheDebugContent}.
 *
 * @module cache-debug/CacheDebugOverlay
 */
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { Button, Text } from '@chatsdet/ui';
import { CacheDebugContent } from './CacheDebugContent';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  runId: string;
};

export function CacheDebugOverlay({ isOpen, onClose, workflowId, runId }: Props) {
  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-40 bg-background"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-4 left-4 z-50"
      >
        <Button
          variant="ghost"
          size="sm"
          onPress={onClose}
          className="w-9 p-0"
          aria-label="Close cache debug"
        >
          <X size={20} weight="bold" />
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex flex-col items-center"
      >
        <div className="w-full max-w-4xl px-6 pt-6 pb-4 shrink-0">
          <Text weight="semibold" size="lg" className="mb-1">
            Cache Debug
          </Text>
        </div>

        <div className="w-full max-w-4xl flex-1 min-h-0 px-6 pb-6">
          <CacheDebugContent workflowId={workflowId} runId={runId} enabled={isOpen} />
        </div>
      </motion.div>
    </>
  );
}
